import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Loader2, RefreshCw, Sparkles, Download, Eye, EyeOff, Save, X, Search, Check } from "lucide-react";

function Row({ label, val }) {
  return (<div className="flex justify-between text-sm py-1 border-b border-[#F0ECE4]"><span className="text-[#5C5F5C]">{label}</span><b className="text-[#1A1C1A]">{val}</b></div>);
}

function PreviewModal({ report, onClose, onDownload }) {
  const d = report.data || {};
  const att = d.attendance || {};
  const t = d.tryout;
  const fmt = (v) => (v === null || v === undefined ? "-" : typeof v === "number" ? Math.sign(v) * Math.round((Math.abs(v) + 1e-9) * 10) / 10 : v);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="preview-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading font-bold text-lg text-[#1A1C1A]">Preview Laporan — {report.name}</h2>
          <button onClick={onClose} data-testid="close-preview-btn" className="p-1 text-[#9C9A94] hover:text-[#1A1C1A]"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs text-[#9C9A94] mb-4">{report.kelas ? `Kelas ${report.kelas} · ` : ""}{d.sessions ?? 0} sesi postes</p>

        <h3 className="font-heading font-bold text-sm text-[#1A1C1A] mb-1">1. Rekap Kehadiran</h3>
        <div className="mb-4">
          <Row label="Hadir" val={att.hadir || 0} /><Row label="Sakit" val={att.sakit || 0} /><Row label="Izin" val={att.izin || 0} /><Row label="Alpa" val={att.alpa || 0} /><Row label="Total Sesi" val={att.total || 0} />
        </div>

        <h3 className="font-heading font-bold text-sm text-[#1A1C1A] mb-1">2. Rekap Nilai Postes Harian</h3>
        <div className="overflow-x-auto mb-2">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#5C5F5C] border-b border-[#E5E0D8]"><th className="py-1 pr-2">Tanggal</th><th className="pr-2">Mapel</th><th className="pr-2">Benar</th><th className="pr-2">Salah</th><th>Skor</th></tr></thead>
            <tbody>{(d.postes || []).map((p, i) => (<tr key={i} className="border-b border-[#F0ECE4]"><td className="py-1 pr-2">{p.tanggal || "-"}</td><td className="pr-2">{p.mapel || "-"}</td><td className="pr-2">{fmt(p.benar)}</td><td className="pr-2">{fmt(p.salah)}</td><td>{fmt(p.skor)}</td></tr>))}</tbody>
          </table>
        </div>
        <p className="text-sm mb-4"><b>Rata-rata: {fmt(d.avg)}{d.avg != null ? "%" : ""}</b> — Kategori: <b>{d.category || "-"}</b>{d.remedial ? <span className="text-[#D9381E]"> · Disarankan remedial</span> : null}</p>

        <h3 className="font-heading font-bold text-sm text-[#1A1C1A] mb-1">3. Ringkasan Simulasi UTBK Terakhir</h3>
        {!t ? <p className="text-sm text-[#9C9A94] mb-4">Belum ada data Simulasi UTBK.</p> : (
          <div className="mb-4">
            <p className="text-sm font-semibold">{t.nama}{t.prev_nama ? <span className="text-xs text-[#9C9A94]"> (vs {t.prev_nama})</span> : null}</p>
            <p className="text-xs text-[#5C5F5C] mb-1">{t.total_irt != null ? `Total IRT ${t.total_irt}` : ""}{t.persen_benar != null ? ` · Benar ${t.persen_benar}%` : ""}</p>
            {!t.incomplete && <table className="w-full text-sm">
              <thead><tr className="text-left text-[#5C5F5C] border-b border-[#E5E0D8]"><th className="py-1 pr-2">Subtes</th><th className="pr-2">Skor</th><th className="pr-2">Lalu</th><th>Δ</th></tr></thead>
              <tbody>{(t.subtes || []).map((s, i) => (<tr key={i} className="border-b border-[#F0ECE4]"><td className="py-1 pr-2">{s.nama}</td><td className="pr-2">{fmt(s.skor)}</td><td className="pr-2">{fmt(s.prev)}</td><td>{s.delta == null ? "-" : (s.delta > 0 ? `+${fmt(s.delta)}` : fmt(s.delta))}</td></tr>))}</tbody>
            </table>}
            {t.incomplete ? <p className="text-sm mt-1 font-medium text-[#E25E3E]" data-testid="sim-incomplete-note">Data simulasi belum lengkap</p> :
            <p className={`text-sm mt-1 font-medium ${t.lulus === true ? "text-[#4A6B53]" : "text-[#E25E3E]"}`}>{t.lulus === true ? `Lulus di ${t.kelulusan || t.lulus_pilihan || "prodi pilihan"}${t.lulus_pilihan ? ` (${t.lulus_pilihan})` : ""}` : "Belum berhasil lulus pada salah satu prodi pilihan"}</p>}
          </div>
        )}

        <h3 className="font-heading font-bold text-sm text-[#1A1C1A] mb-1">4. Analisa &amp; Catatan</h3>
        <p className="text-sm text-[#1A1C1A] whitespace-pre-wrap mb-5">{report.analisa || "-"}</p>

        <div className="flex justify-end gap-2">
          <button onClick={onDownload} data-testid="preview-download-btn" className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm text-[#5C5F5C] inline-flex items-center gap-2"><Download className="h-4 w-4" /> Unduh PDF</button>
          <button onClick={onClose} className="px-4 py-2 rounded-full bg-[#1A1C1A] text-white text-sm">Tutup</button>
        </div>
      </div>
    </div>
  );
}

const CAT_CLS = {
  Perfect: "bg-[#4A6B53] text-white", "Lulus Grade A": "bg-[#4A6B53]/15 text-[#4A6B53]",
  "Lulus Grade B": "bg-[#F4B942]/25 text-[#8a6d16]", "Lulus Grade C": "bg-[#E2A03E]/20 text-[#8a6d16]",
  "Kurang (Belum Lulus)": "bg-[#D9381E]/15 text-[#D9381E]",
};

export default function AdminLaporanBulanan() {
  const [students, setStudents] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState("");
  const [gen, setGen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [previewed, setPreviewed] = useState({});
  const [preview, setPreview] = useState(null);
  const [q, setQ] = useState("");
  const [showList, setShowList] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/monthly/reports"),
      ]);
      setStudents((u.data.users || []).filter((x) => x.role === "siswa"));
      setReports(r.data.reports || []);
    } catch { toast.error("Gagal memuat data"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const term = q.trim().toLowerCase();
  const filtered = term
    ? students.filter((s) => `${s.name} ${s.email} ${s.kelas || ""}`.toLowerCase().includes(term))
    : students;

  const generate = async () => {
    if (!sel) { toast.error("Pilih siswa dulu"); return; }
    setGen(true);
    try {
      const { data } = await api.post("/admin/monthly/generate", { user_id: sel });
      toast.success("Laporan berhasil digenerate (draft, belum dipublikasikan)");
      setSel("");
      if (data?.report?.id) setPreviewed((p) => { const n = { ...p }; delete n[data.report.id]; return n; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal generate"); }
    setGen(false);
  };

  const openEdit = (r) => { setOpenId(openId === r.id ? null : r.id); setDraft(r.analisa || ""); };

  const [regenBusy, setRegenBusy] = useState(false);
  const regenAll = async () => {
    if (!window.confirm("Regenerate ulang SEMUA laporan bulanan yang sudah ada? Data (kategori, rata-rata, periode, kelas) dihitung ulang. Analisa otomatis ikut diperbarui (kecuali yang sudah diedit manual). Status publikasi tetap dipertahankan.")) return;
    setRegenBusy(true);
    try {
      const { data } = await api.post("/admin/monthly/regenerate-all", {});
      toast.success(`${data.regenerated} laporan diperbarui${data.skipped ? ` · ${data.skipped} dilewati` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal regenerate massal"); }
    setRegenBusy(false);
  };

  const saveAnalisa = async (r) => {
    setBusyId(r.id);
    try {
      await api.put(`/admin/monthly/${r.id}`, { analisa: draft });
      toast.success("Analisa disimpan");
      setReports((prev) => prev.map((x) => x.id === r.id ? { ...x, analisa: draft } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    setBusyId(null);
  };

  const togglePublish = async (r) => {
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/admin/monthly/${r.id}/publish`, { published: !r.published });
      toast.success(data.published ? (data.notified ? "Laporan dipublikasikan — email notifikasi dikirim ke siswa & orang tua" : "Laporan dipublikasikan — siswa & orang tua kini bisa mengunduh") : "Publikasi ditarik");
      setReports((prev) => prev.map((x) => x.id === r.id ? { ...x, published: data.published } : x));
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah publikasi"); }
    setBusyId(null);
  };

  const downloadPdf = async (r) => {
    try { await downloadFile(`/admin/monthly/${r.id}/pdf`, `Laporan-${r.name || "Siswa"}.pdf`); }
    catch { toast.error("Gagal membuka PDF"); }
  };

  const openPreview = async (r) => {
    try {
      const { data } = await api.get(`/admin/monthly/${r.id}/detail`);
      setPreview(data.report);
      setPreviewed((p) => ({ ...p, [r.id]: true }));
    } catch { toast.error("Gagal memuat preview"); }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="admin-laporan-bulanan">
        <div className="flex items-center justify-between mb-1">
          <h1 className="font-heading font-black text-3xl text-[#1A1C1A]">Laporan Bulanan</h1>
          <div className="flex items-center gap-3">
            <button onClick={regenAll} disabled={regenBusy} data-testid="regenerate-all-btn"
              className="text-sm text-white bg-[#4A6B53] px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 hover:bg-[#3d5946] disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${regenBusy ? "animate-spin" : ""}`} /> {regenBusy ? "Memproses…" : "Regenerate Semua"}
            </button>
            <button onClick={load} data-testid="refresh-reports" className="text-sm text-[#5C5F5C] inline-flex items-center gap-1 hover:text-[#1A1C1A]">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Muat ulang
            </button>
          </div>
        </div>
        <p className="text-[#5C5F5C] mb-5">Generate laporan dari data postes yang ada (meski belum 16 sesi), edit teks analisa, lalu <b>Publikasikan</b> agar bisa diunduh siswa & orang tua.</p>

        <div className="clay-card p-5 mb-6">
          <label className="text-sm font-medium text-[#1A1C1A]">Generate Laporan untuk siswa:</label>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <div className="relative flex-1 min-w-[220px]">
              <div className="flex items-center h-10 px-3 rounded-xl border border-[#E5E0D8] bg-white focus-within:border-[#E25E3E]">
                <Search className="h-4 w-4 text-[#9C9A94] shrink-0" />
                <input
                  data-testid="student-search-input"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setSel(""); setShowList(true); }}
                  onFocus={() => setShowList(true)}
                  onBlur={() => setTimeout(() => setShowList(false), 150)}
                  placeholder="Ketik nama / email siswa…"
                  className="flex-1 h-full px-2 bg-transparent outline-none text-sm"
                />
                {q && (
                  <button type="button" onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setQ(""); setSel(""); setShowList(false); }}
                    data-testid="student-search-clear" className="p-0.5 text-[#9C9A94] hover:text-[#1A1C1A]"><X className="h-4 w-4" /></button>
                )}
              </div>
              {showList && (
                <div data-testid="student-search-list" className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-[#E5E0D8] rounded-xl shadow-lg">
                  {filtered.length === 0 ? (
                    <div className="px-3 py-2.5 text-sm text-[#9C9A94]">Tidak ada siswa cocok.</div>
                  ) : filtered.slice(0, 50).map((s) => (
                    <button key={s.id} type="button" onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setSel(s.id); setQ(`${s.name}${s.kelas ? ` (Kelas ${s.kelas})` : ""}`); setShowList(false); }}
                      data-testid={`student-option-${s.id}`}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F7F4EE] flex items-center justify-between gap-2 ${sel === s.id ? "bg-[#F7F4EE]" : ""}`}>
                      <span className="min-w-0">
                        <span className="font-medium text-[#1A1C1A]">{s.name}</span>{s.kelas ? <span className="text-[#9C9A94]"> · Kelas {s.kelas}</span> : ""}
                        <span className="block text-xs text-[#9C9A94] truncate">{s.email}</span>
                      </span>
                      {sel === s.id && <Check className="h-4 w-4 text-[#E25E3E] shrink-0" />}
                    </button>
                  ))}
                  {filtered.length > 50 && <div className="px-3 py-2 text-xs text-[#9C9A94]">Menampilkan 50 dari {filtered.length}. Persempit pencarian…</div>}
                </div>
              )}
            </div>
            <button onClick={generate} disabled={gen || !sel} data-testid="generate-btn"
              className="h-10 px-5 rounded-full bg-[#E25E3E] text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-[#C94F31] disabled:opacity-50">
              {gen ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Generate Laporan Bulanan
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 grid place-items-center text-[#9C9A94]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : reports.length === 0 ? (
          <p className="text-sm text-[#9C9A94]" data-testid="no-reports">Belum ada laporan. Pilih siswa lalu klik Generate.</p>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <div key={r.id} className="clay-card p-4" data-testid={`report-${r.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1A1C1A] truncate">{r.name || "-"}</span>
                      {r.kelas ? <span className="text-xs text-[#9C9A94]">Kelas {r.kelas}</span> : null}
                      {r.published
                        ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#4A6B53] text-white" data-testid={`status-${r.id}`}>Dipublikasikan</span>
                        : <span className="text-xs px-2 py-0.5 rounded-full bg-[#F0ECE4] text-[#5C5F5C]" data-testid={`status-${r.id}`}>Draft</span>}
                    </div>
                    <p className="text-xs text-[#9C9A94] mt-1">
                      {r.sessions ?? 0} sesi postes · Rata-rata {r.avg != null ? `${r.avg}%` : "-"}
                      {r.category ? <span className={`ml-1 px-1.5 py-0.5 rounded ${CAT_CLS[r.category] || "bg-[#F0ECE4]"}`}>{r.category}</span> : null}
                    </p>
                    {!r.published && !previewed[r.id] && <p className="text-[11px] text-[#E25E3E] mt-0.5" data-testid={`hint-${r.id}`}>Lihat preview dulu untuk mengaktifkan tombol Publikasikan.</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openPreview(r)} data-testid={`preview-${r.id}`} title="Lihat preview lengkap" className="px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1 bg-[#1A1C1A] text-white"><Eye className="h-3.5 w-3.5" /> Preview</button>
                    <button onClick={() => openEdit(r)} data-testid={`edit-${r.id}`} title="Edit analisa" className="p-2 rounded-lg text-[#1A1C1A] hover:bg-[#1A1C1A]/8"><FileText className="h-4 w-4" /></button>
                    <button onClick={() => downloadPdf(r)} data-testid={`download-${r.id}`} title="Unduh PDF" className="p-2 rounded-lg text-[#4A6B53] hover:bg-[#4A6B53]/10"><Download className="h-4 w-4" /></button>
                    {(() => {
                      const canPublish = r.published || previewed[r.id];
                      return (
                        <button onClick={() => canPublish && togglePublish(r)} disabled={busyId === r.id || !canPublish} data-testid={`publish-${r.id}`}
                          title={r.published ? "Tarik publikasi" : (canPublish ? "Publikasikan" : "Lihat preview dulu untuk mengaktifkan")}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1 disabled:cursor-not-allowed ${r.published ? "border border-[#E5E0D8] text-[#5C5F5C]" : canPublish ? "bg-[#4A6B53] text-white" : "bg-[#E5E0D8] text-[#9C9A94]"}`}>
                          {r.published ? <><EyeOff className="h-3.5 w-3.5" /> Tarik</> : <><Eye className="h-3.5 w-3.5" /> Publikasikan</>}
                        </button>
                      );
                    })()}
                  </div>
                </div>
                {openId === r.id && (
                  <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid={`editor-${r.id}`}>
                    <label className="text-xs font-medium text-[#5C5F5C]">Teks Analisa (muncul di bagian "Analisa & Catatan" pada PDF)</label>
                    <textarea value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`analisa-input-${r.id}`} rows={6}
                      className="w-full mt-1 p-3 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm leading-relaxed" />
                    <div className="flex justify-end mt-2">
                      <button onClick={() => saveAnalisa(r)} disabled={busyId === r.id} data-testid={`save-analisa-${r.id}`}
                        className="px-4 py-2 rounded-full bg-[#1A1C1A] text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                        <Save className="h-4 w-4" /> {busyId === r.id ? "Menyimpan…" : "Simpan Analisa"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {preview && <PreviewModal report={preview} onClose={() => setPreview(null)} onDownload={() => downloadPdf(preview)} />}
    </AppShell>
  );
}
